import { z } from 'zod';
import type { Credits } from './api';
import { DOC_BLOCK_TYPES, validateBlock, type Block, type BlockType } from './blocks';
import type { StudioToolMeta } from './studio';

/* ─────────────────────────────────────────────────────────────────────
   A conversa com o agente sobre uma peça pronta.
   O agente não reescreve a peça: devolve OPERAÇÕES sobre os blocos —
   trocar, inserir, remover, renomear. Cada operação é validada no
   instante em que chega (o bloco passa pelo mesmo Zod da geração) e
   aplicada ao vivo no canvas. Os índices se referem sempre à versão
   de onde a conversa partiu, então a ordem das operações não importa.
   Cada rodada que muda alguma coisa vira uma versão da peça.
   ───────────────────────────────────────────────────────────────────── */

export type EditOp =
  | { op: 'replace'; at: number; block: Block }
  | { op: 'insert'; after: number; block: Block }
  | { op: 'delete'; at: number }
  | { op: 'title'; title: string };

const rawOpSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('replace'), at: z.number().int().min(0), block: z.unknown() }),
  z.object({ op: z.literal('insert'), after: z.number().int().min(-1), block: z.unknown() }),
  z.object({ op: z.literal('delete'), at: z.number().int().min(0) }),
  z.object({ op: z.literal('title'), title: z.string().trim().min(1).max(160) }),
]);

/** Que blocos cabem numa peça: documento só tem blocos de documento; landing não tem páginas. */
export function blockFits(type: BlockType, tool: Pick<StudioToolMeta, 'key' | 'output'>): boolean {
  if (tool.output === 'document') return DOC_BLOCK_TYPES.includes(type);
  if (type === 'page') return tool.key === 'site';
  return true;
}

/** Valida uma operação crua contra a peça (tamanho e tipo). */
export function parseEditOp(input: unknown, size: number, tool: Pick<StudioToolMeta, 'key' | 'output'>):
  { ok: true; op: EditOp } | { ok: false; error: string } {
  const r = rawOpSchema.safeParse(input);
  if (!r.success) return { ok: false, error: r.error.issues.map(i => `${i.path.join('.') || 'op'}: ${i.message}`).join('; ') };
  const o = r.data;
  if (o.op === 'title') return { ok: true, op: o };
  if (o.op === 'insert' ? o.after >= size : o.at >= size) return { ok: false, error: `índice fora da peça (${size} blocos)` };
  if (o.op === 'delete') return { ok: true, op: o };
  const b = validateBlock(o.block);
  if (!b.ok) return { ok: false, error: b.error };
  if (!blockFits(b.block.type, tool)) return { ok: false, error: `bloco "${b.block.type}" não cabe nesta peça` };
  return { ok: true, op: o.op === 'replace' ? { op: 'replace', at: o.at, block: b.block } : { op: 'insert', after: o.after, block: b.block } };
}

export interface EditResult {
  blocks: Block[];
  /** novo título, se alguma operação o trocou */
  title: string | null;
  /** índices, na peça nova, dos blocos que mudaram ou chegaram */
  fresh: number[];
}

/**
 * Aplica as operações à versão de partida. Para o mesmo índice vale a
 * última operação (trocar depois de remover recupera o bloco).
 * Inserções no mesmo ponto entram na ordem em que chegaram.
 */
export function applyEdits(blocks: readonly Block[], ops: readonly EditOp[]): EditResult {
  const replaced = new Map<number, Block>();
  const deleted = new Set<number>();
  const inserts = new Map<number, Block[]>();
  let title: string | null = null;
  for (const o of ops) {
    if (o.op === 'title') title = o.title;
    else if (o.op === 'replace') { replaced.set(o.at, o.block); deleted.delete(o.at); }
    else if (o.op === 'delete') { deleted.add(o.at); replaced.delete(o.at); }
    else inserts.set(o.after, [...(inserts.get(o.after) ?? []), o.block]);
  }
  const out: Block[] = [];
  const fresh: number[] = [];
  const add = (b: Block, isNew: boolean) => { if (isNew) fresh.push(out.length); out.push(b); };
  for (const b of inserts.get(-1) ?? []) add(b, true);
  blocks.forEach((b, i) => {
    if (!deleted.has(i)) {
      const r = replaced.get(i);
      add(r ?? b, !!r);
    }
    for (const x of inserts.get(i) ?? []) add(x, true);
  });
  return { blocks: out, title, fresh };
}

/* ── como cada mudança aparece na conversa ─────────────────────────── */

export type EditChangeKind = 'replace' | 'insert' | 'delete' | 'title';
export interface EditChange { kind: EditChangeKind; label: string }

const clip = (s: string, n = 44) => {
  const p = s.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\s+/g, ' ').trim();
  return p.length <= n ? p : `${p.slice(0, n - 1).replace(/[\s,.;:—-]+$/, '')}…`;
};

/** O nome de um bloco como a pessoa o reconhece na peça. */
export function blockLabel(b: Block): string {
  switch (b.type) {
    case 'section': return `Seção “${clip(b.title)}”`;
    case 'page': return `Página “${clip(b.title)}”`;
    case 'hero': return `Abertura “${clip(b.title, 36)}”`;
    case 'features': return b.title ? `“${clip(b.title)}”` : 'Diferenciais';
    case 'steps': return b.title ? `“${clip(b.title)}”` : 'Como funciona';
    case 'stats': return 'Números';
    case 'testimonial': return 'Depoimento';
    case 'faq': return b.title ? `“${clip(b.title)}”` : 'Perguntas frequentes';
    case 'cta': return `Chamada “${clip(b.title, 36)}”`;
    case 'gallery': return b.title ? `Galeria “${clip(b.title)}”` : `Galeria com ${b.items.length} fotos`;
    case 'image': return b.alt ? `Foto “${clip(b.alt)}”` : 'Foto';
    case 'heading': return `Subtítulo “${clip(b.text)}”`;
    case 'paragraph': return `Parágrafo “${clip(b.text, 36)}”`;
    case 'list': return 'Lista';
    case 'metric': return `Indicador “${clip(b.label)}”`;
    case 'table': return b.caption ? `Tabela “${clip(b.caption)}”` : 'Tabela';
    case 'chart': return b.title ? `Gráfico “${clip(b.title)}”` : 'Gráfico';
    case 'callout': return 'Nota';
    case 'quote': return 'Citação';
  }
}

/** A mudança, em uma linha, para a lista da conversa. */
export function describeEdit(op: EditOp, base: readonly Block[]): EditChange {
  switch (op.op) {
    case 'title': return { kind: 'title', label: `Título: “${clip(op.title, 60)}”` };
    case 'replace': return { kind: 'replace', label: blockLabel(op.block) };
    case 'insert': return { kind: 'insert', label: blockLabel(op.block) };
    case 'delete': return { kind: 'delete', label: base[op.at] ? blockLabel(base[op.at]!) : 'Bloco' };
  }
}

/* ── o pedido ──────────────────────────────────────────────────────── */

export const CHAT_MAX_ATTACHMENTS = 8;
const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'anexo inválido');

export const chatRequestSchema = z.object({
  text: z.string().trim().max(4000).default(''),
  attachments: z.array(uuid).max(CHAT_MAX_ATTACHMENTS, `até ${CHAT_MAX_ATTACHMENTS} anexos por mensagem`).default([]),
}).refine(r => r.text.length > 0 || r.attachments.length > 0, { message: 'Escreva um pedido ou envie um arquivo.', path: ['text'] });
export type ChatRequest = z.infer<typeof chatRequestSchema>;

/** Quanto custa uma rodada que muda a peça: um décimo da geração, no mínimo 5. Conversa sem mudança não custa. */
export const editCost = (tool: Pick<StudioToolMeta, 'cost'>) => Math.max(5, Math.round(tool.cost / 10));

/** Imagens que o agente pode pôr na peça (sem SVG: nada que execute). */
export const MEDIA_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export const isMediaType = (t: string) => (MEDIA_TYPES as readonly string[]).includes(t);

/* ── o que a API devolve ───────────────────────────────────────────── */

export interface ChatAttachmentDto {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  /** foto que pode entrar na peça */
  image: boolean;
  width: number | null;
  height: number | null;
}

export type ChatMessageStatus = 'running' | 'done' | 'failed';

export interface ChatMessageDto {
  id: string;
  role: 'user' | 'agent';
  text: string;
  attachments: ChatAttachmentDto[];
  changes: EditChange[];
  status: ChatMessageStatus;
  error: string | null;
  /** a versão que esta resposta criou */
  revision: { id: string; n: number } | null;
  cost: number;
  createdAt: string;
  createdBy: string | null;
}

export type RevisionSource = 'generation' | 'chat' | 'restore';

export interface RevisionDto {
  id: string;
  n: number;
  title: string;
  source: RevisionSource;
  note: string | null;
  blocks: number;
  createdAt: string;
  createdBy: string | null;
}

export interface ConversationDto {
  messages: ChatMessageDto[];
  /** da mais nova para a mais antiga */
  revisions: RevisionDto[];
  /** a versão que está na peça agora */
  head: number;
  /** a resposta que o agente está escrevendo, se houver */
  running: string | null;
  /** quanto custa uma rodada que muda a peça */
  editCost: number;
}

/* ── eventos do stream da conversa (SSE) ─────────────────────────── */

export interface EditStartEvent { request: ChatMessageDto; reply: ChatMessageDto; base: number }
export interface EditPhaseEvent { label: string }
export interface EditReplyEvent { text: string }
export interface EditOpEvent { index: number; op: EditOp; change: EditChange }
export interface EditUsageEvent { tokens: number }
export interface EditDoneEvent { reply: ChatMessageDto; revision: RevisionDto | null; title: string; credits: Credits }
export interface EditErrorEvent { message: string; code?: string }

export interface EditEventMap {
  start: EditStartEvent;
  phase: EditPhaseEvent;
  reply: EditReplyEvent;
  op: EditOpEvent;
  usage: EditUsageEvent;
  done: EditDoneEvent;
  error: EditErrorEvent;
}
export type EditEventName = keyof EditEventMap;
export type EditEvent = { [K in EditEventName]: { event: K; data: EditEventMap[K] } }[EditEventName];
